import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUpload } from '../../contexts/UploadContext';
import { useTheme } from '../../contexts/ThemeContext';
import './Layout.css';

const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const { hasActiveUploads, canLogout } = useUpload();
    const { theme, toggleTheme } = useTheme();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [showLogoutWarning, setShowLogoutWarning] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    useEffect(() => {
        if (!profileOpen) return;
        const handleClickOutside = () => setProfileOpen(false);
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, [profileOpen]);

    const handleLogout = async () => {
        if (!canLogout()) {
            setShowLogoutWarning(true);
            return;
        }
        await logout();
        // VMS is SSO-only — send the user back to the QuirkyQode portal
        // rather than a local login page (there isn't one).
        window.location.href = process.env.REACT_APP_QQ_PORTAL_URL || 'https://quirkyqode.com/my-tools';
    };



    const navItems = [
        { path: '/', label: 'Dashboard', icon: 'fa-th-large', section: 'operator' },
        { path: '/recording', label: 'Record Video', icon: 'fa-video', section: 'operator' },
        { path: '/feeds', label: 'My Feeds', icon: 'fa-list', section: 'operator' },
        { path: '/uploading', label: 'Uploading', icon: 'fa-upload', section: 'operator' },
        { path: '/admin/feeds', label: 'All Feeds', icon: 'fa-server', section: 'admin' },
        { path: '/admin/failed', label: 'Failed Uploads', icon: 'fa-exclamation-triangle', section: 'admin' },
        { path: '/admin/uploading', label: 'All Uploading', icon: 'fa-cloud-upload-alt', section: 'admin' },
    ];

    return (
        <div className="layout">
            {/* Sidebar */}
            <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
                <div className="sidebar-header">
                    <div className="logo">
                        <div className="logo-icon">
                            <i className="fas fa-video"></i>
                        </div>
                        <span className="logo-text">VMS</span>
                    </div>
                    <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
                        <i className={`fas fa-chevron-${sidebarOpen ? 'left' : 'right'}`}></i>
                    </button>
                </div>

                <nav className="sidebar-nav">
                    <div className="nav-section">
                        <span className="nav-section-title">Menu</span>
                        {navItems.filter(item => item.section === 'operator').map(item => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            >
                                <span className="nav-icon"><i className={`fas ${item.icon}`}></i></span>
                                <span className="nav-label">{item.label}</span>
                            </NavLink>
                        ))}
                    </div>

                    {user?.role === 'admin' && (
                        <div className="nav-section">
                            <span className="nav-section-title">Admin</span>
                            {navItems.filter(item => item.section === 'admin').map(item => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                >
                                    <span className="nav-icon"><i className={`fas ${item.icon}`}></i></span>
                                    <span className="nav-label">{item.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    )}
                </nav>

                <div className="sidebar-footer">
                    <button
                        className={`sidebar-logout-btn ${hasActiveUploads ? 'disabled' : ''}`}
                        onClick={handleLogout}
                        title={hasActiveUploads ? "Cannot logout during active upload" : "Logout"}
                        disabled={hasActiveUploads}
                    >
                        <i className="fas fa-sign-out-alt"></i>
                        <span className="nav-label">Logout</span>
                    </button>
                    <div className="sidebar-user-info">
                        <div className="sidebar-user-avatar">
                            {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                        </div>
                        <div className="user-details">
                            <span className="user-name">{user?.name || user?.email?.split('@')[0]}</span>
                            <span className="user-role">{user?.role}</span>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                <header className="top-header">
                    {hasActiveUploads && (
                        <div className="upload-indicator">
                            <span className="upload-dot"></span>
                            Upload in progress
                        </div>
                    )}

                    <button
                        className="theme-toggle-btn"
                        onClick={toggleTheme}
                        title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                    >
                        <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`}></i>
                    </button>

                    <div className="profile-menu">
                        <div
                            className="profile-chip"
                            onClick={(e) => {
                                e.stopPropagation();
                                setProfileOpen(o => !o);
                            }}
                        >
                            <div className="header-user-avatar">
                                {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                            </div>
                            <div className="header-user-details">
                                <span className="header-email">{user?.name || user?.email?.split('@')[0]}</span>
                                <span className="header-user-role">{user?.role}</span>
                            </div>
                            <i className="fas fa-chevron-down profile-chevron"></i>
                        </div>

                        {profileOpen && (
                            <div className="profile-dropdown" onClick={(e) => e.stopPropagation()}>
                                <div className="profile-dropdown-header">
                                    <p className="profile-dropdown-label">Signed in as</p>
                                    <p className="profile-dropdown-email">{user?.email}</p>
                                    <span className="profile-dropdown-role">Role: {user?.role}</span>
                                </div>
                                <button
                                    className={`profile-dropdown-logout ${hasActiveUploads ? 'disabled' : ''}`}
                                    onClick={() => {
                                        setProfileOpen(false);
                                        handleLogout();
                                    }}
                                    title={hasActiveUploads ? "Cannot logout during active upload" : "Logout"}
                                >
                                    <i className="fas fa-sign-out-alt"></i>
                                    Log Out
                                </button>
                            </div>
                        )}
                    </div>
                </header>
                <div className="content-area">
                    {children}
                </div>
            </main>

            {/* Logout Warning Modal */}
            {showLogoutWarning && (
                <div className="modal-overlay" onClick={() => setShowLogoutWarning(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Upload in Progress</h2>
                        </div>
                        <div className="modal-body">
                            <p>You have active uploads in progress. Please wait for them to complete before logging out.</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn btn-primary"
                                onClick={() => setShowLogoutWarning(false)}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Layout;
